import { errorMessage, finalExp, passwordErrorMessage } from 'utils/validation'
import { fetchPreregister, selectRegistrationData } from 'containers/Login/loginSlice'
import { selectForm, updateValue, updateValues } from '../formSlice'
import ConsentPreviewPopup from 'components/popups/ConsentPreview'
import { useDispatch, useSelector } from 'react-redux'
import PhoneInput from 'components/inputs/PhoneInput'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import Checkbox from 'components/inputs/Checkbox'
import usePopupState from 'hooks/usePopupState'
import Input from 'components/inputs/Input'
import { isValidPhone } from 'utils/phone'
import { useEffect, useRef, useState } from 'react'

const Form = ({ block, submit, warning, setWarning, showWarning, showConsentPopup }) => {
  const registration = useSelector(selectRegistrationData)
  const { firstName = '', lastName = '', mail = '', phone = '', prefix, rulesAgreement = false } = useSelector(selectForm)
  const [passwords, setPasswords] = useState({
    first: '',
    second: '',
  })
  const refs = []
  // cache sprawdzeń w bazie: { [name]: { key, promise } } – bez ponownych zapytań o tę samą wartość
  const preregisterChecks = useRef({})
  const mounted = useRef(false)

  const dispatch = useDispatch()
  const { t } = useTranslation('form')
  const { popup, openPopup } = usePopupState()

  const consentArray = t('IAcceptTheTermsOfServiceAndConfirmThatIHaveReadThePrivacyPolicyArray')
  const changeHandler = e => {
    if (!e) return
    const { name, value, checked, type } = e.target
    const newValue = type === 'checkbox' ? checked : value
    if (name.includes('password')) {
      setPasswords({
        ...passwords,
        [name === 'password' ? 'first' : 'second']: value
      })
    } else {
      dispatch(updateValue({ name, value: newValue }))
    }

    if (newValue && warning[name] && (
      (name === 'rulesAgreement') ||
      (name === 'firstName' && newValue.length > 2) ||
      (name === 'lastName' && newValue.length > 1) ||
      (name === 'mail' && newValue.match(finalExp.mail)) ||
      (name === 'password' && newValue.match(finalExp.password)) ||
      (name === 'passwordSec' && newValue === passwords.first)))
      setWarning(prev => ({
        ...prev,
        [name]: false,
      }))
  }

  const blurHandler = e => {
    if (!e) return
    const { name, value } = e.target
    let warn = false

    switch (name) {
      case 'firstName':
        warn = value.length < 3 ? errorMessage(t, 3 - value.length) : false
        break

      case 'lastName':
        warn = value.length < 2 ? errorMessage(t, 2 - value.length) : false
        break

      case 'mail':
        if (value.match(finalExp.mail)) checkFormData(name, { email: value })
        else warn = t('wrongEmail')
        break

      case 'phone':
        if (!phone) warn = t('enterPhoneNumber')
        else if (!isValidPhone(prefix, phone)) warn = t('wrongNumber')
        else checkFormData(name, { prefix, phone })
        break

      case 'password':
        warn = passwordErrorMessage(value, t)
        break

      case 'passwordSec':
        warn = passwords.first !== passwords.second ? t('passwordsDoNotMatch') : false
        break

      default: warn = false
    }

    setWarning(prev => prev[name] === warn ? prev : {
      ...prev,
      [name]: warn,
    })
  }

  const checkFormData = async (name, body) => {
    const key = JSON.stringify(body)
    let check = preregisterChecks.current[name]

    if (check?.key !== key) {
      const promise = dispatch(fetchPreregister(body)).unwrap()
        .then(response => response?.valid ?? true)
        .catch(() => {
          console.warn('Fetch preregister failed')
          // nie cache'uj błędu – kolejny blur/zmiana spróbuje ponownie
          if (preregisterChecks.current[name]?.key === key) preregisterChecks.current[name] = null
          return true
        })
      check = preregisterChecks.current[name] = { key, promise }
    }

    const valid = await check.promise
    // wartość zdążyła się zmienić w trakcie zapytania – wynik nieaktualny
    if (preregisterChecks.current[name]?.key !== key) return

    if (!valid) {
      if (document.activeElement?.name === name) document.activeElement.blur()
      setWarning(prev => ({
        ...prev,
        [name]: t('alreadyInUse'),
      }))
      showWarning(name === 'mail')
    }
  }

  // Autouzupełnianie przeglądarki wypełnia pola bez fokusu, więc onBlur się dla nich nie wykona.
  // Zmiana wartości w polu, które nie ma fokusu = autofill -> uruchamiamy tę samą walidację co na blur.
  const isFocused = name => document.activeElement?.name === name

  useEffect(() => {
    if (!mounted.current || !mail || registration?.email || block || isFocused('mail')) return
    blurHandler({ target: { name: 'mail', value: mail } })
  }, [mail]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mounted.current || !phone || registration?.phone || block || isFocused('phone')) return
    blurHandler({ target: { name: 'phone', value: phone } })
  }, [phone]) // eslint-disable-line react-hooks/exhaustive-deps

  // musi być po efektach powyżej – pomijamy wartości obecne już przy montowaniu formularza
  useEffect(() => { mounted.current = true }, [])

  const enterClicked = ref => {
    const index = refs.findIndex(r => Object.is(r, ref))
    if (index === -1) return
    if (refs[index + 1]) refs[index + 1].focus()
    else if (index + 1 === refs.length && !block) validation()
  }

  const validation = () => {
    const passwordValid = registration ? true : (passwords.first.match(finalExp.password) && passwords.second === passwords.first)
    const registrationFormValid = rulesAgreement && firstName.length > 2 && lastName.length > 1 && mail.match(finalExp.mail) && isValidPhone(prefix, phone) && passwordValid

    if (registrationFormValid) {
      const body = {
        first_name: firstName,
        surname: lastName,
        email: mail,
        prefix,
        phone,
        terms: rulesAgreement
      }
      if (!registration) {
        body.password = passwords.first
        body.password_confirmation = passwords.second
      }
      submit(body)

    } else {
      const newErrors = {
        firstName: firstName.length < 3 ? errorMessage(t, 3 - firstName.length) : false,
        lastName: lastName.length < 2 ? errorMessage(t, 2 - lastName.length) : false,
        mail: !mail.match(finalExp.mail) ? t('wrongEmail') : false,
        phone: !phone ? t('enterPhoneNumber') : !isValidPhone(prefix, phone) ? t('wrongNumber') : false,
        password: passwordErrorMessage(passwords.first, t),
        passwordSec: passwords.first !== passwords.second ? t('passwordsDoNotMatch') : false,
        rulesAgreement: !rulesAgreement,
      }
      setWarning(newErrors)
    }
  }

  // eslint-disable-next-line jsx-a11y/anchor-is-valid
  const Link = ({ type, children }) => <a
    className='--link'
    onClick={e => {
      e.preventDefault()
      e.stopPropagation()
      if (showConsentPopup) showConsentPopup(type)
      else openPopup({ type: 'consent', content: type })
    }}>
    {children}
  </a>

  return (
    <>
      {popup?.type === 'consent' && <ConsentPreviewPopup
        type={popup.content}
      />}
      <div className='registration__form form'>
        {/* <p className='registration__form__info'>
          {t(`toRentAWarehouseOnlineYouNeedToLogInThisWayYouWillBeAbleToUseTheFullCapabilitiesOfOurSystems.${process.env.REACT_APP_TYPE}`)}
        </p> */}

        <Input
          required
          ref={r => refs.push(r)}
          value={firstName}
          name='firstName'
          label={t('firstName')}
          minLength='3'
          maxLength='100'
          disabled={block}
          autoComplete='given-name'
          warning={warning.firstName}
          placeholder={t('enterFirstName')}
          blurHandler={blurHandler}
          enterClicked={enterClicked}
          changeHandler={changeHandler} />

        <Input
          required
          ref={r => refs.push(r)}
          value={lastName}
          name='lastName'
          label={t('lastName')}
          minLength='2'
          maxLength='100'
          disabled={block}
          autoComplete='family-name'
          warning={warning.lastName}
          placeholder={t('enterLastName')}
          blurHandler={blurHandler}
          enterClicked={enterClicked}
          changeHandler={changeHandler} />

        <Input
          required
          ref={r => refs.push(r)}
          value={mail}
          name='mail'
          label={t('email')}
          minLength='4'
          maxLength='100'
          autoComplete='email'
          disabled={!!registration?.email || block}
          warning={warning.mail}
          placeholder={t('enterEmail')}
          blurHandler={blurHandler}
          enterClicked={enterClicked}
          changeHandler={changeHandler} />

        <PhoneInput
          required
          ref={r => refs.push(r)}
          value={phone}
          prefix={prefix}
          name='phone'
          label={t('phoneNumber')}
          disabled={!!registration?.phone || block}
          warning={warning.phone}
          placeholder={t('enterPhoneNumber')}
          blurHandler={blurHandler}
          enterClicked={enterClicked}
          setValue={value => {
            dispatch(updateValues(value))
            if (warning.phone && isValidPhone(value.prefix, value.phone)) setWarning({ ...warning, phone: false })
          }} />

        {!registration && <>
          <Input
            required
            ref={r => refs.push(r)}
            value={passwords.first}
            name='password'
            type='password'
            label={t('password')}
            minLength='8'
            maxLength='100'
            disabled={block}
            autoComplete='new-password'
            warning={warning.password}
            placeholder={t('enterPassword')}
            blurHandler={blurHandler}
            enterClicked={enterClicked}
            changeHandler={changeHandler} />

          <Input
            required
            ref={r => refs.push(r)}
            value={passwords.second}
            name='passwordSec'
            type='password'
            label={t('repeatPassword')}
            minLength='8'
            maxLength='100'
            disabled={block}
            autoComplete='new-password'
            warning={warning.passwordSec}
            placeholder={t('reEnterPassword')}
            blurHandler={blurHandler}
            enterClicked={enterClicked}
            changeHandler={changeHandler} />
        </>}

        <div className='checkbox__area input__default'>
          <label className='input__label'>
            <span>{t('termsAndConditions')}</span>
          </label>

          <Checkbox
            required
            disabled={block}
            name='rulesAgreement'
            checked={rulesAgreement}
            warning={warning.rulesAgreement}
            label={<>{consentArray[0]} {<Link type='terms'>{consentArray[1]}</Link>} {consentArray[2]} {<Link type='privacy'>{consentArray[3]}</Link>}</>}
            changeHandler={changeHandler} />
        </div>

        <ArrowBtn
          enlarged
          text={t(registration ? 'save' : 'signUp')}
          action={() => !block && validation()} />
      </div>
    </>
  )
}

export default Form